import { widgetApi, getWidgetApiError } from "../services/api";

export type RequestAmountCheckInput = {
  originCountryCode: string;
  destinationCountryCode: string;
  sourceCurrency: string;
  destinationCurrency: string;
  payoutMethod: string;
  payerCode?: string;
  stateCode?: string;
  corridorCode?: string;
  amount: number;
  amountAnchor: "send" | "receive";
};

export type RequestAmountEnvelope = {
  min: number | null;
  max: number | null;
  currency: string;
};

/** Envelope details — send authority + optional receive display projection. */
export type RequestAmountEnvelopeDetails = {
  min?: number;
  max?: number;
  currency?: string;
  minReceive?: number;
  maxReceive?: number;
  receiveCurrency?: string;
};

export type RequestAmountCheckOk = {
  ok: true;
  envelope: RequestAmountEnvelope | null;
  indicativeSend: number | null;
  minReceive?: number | null;
  maxReceive?: number | null;
  receiveCurrency?: string;
};

export type RequestAmountCheckFail = {
  ok: false;
  code: "REQUEST_AMOUNT_BELOW_MIN" | "REQUEST_AMOUNT_ABOVE_MAX" | string;
  details: RequestAmountEnvelopeDetails;
};

type ApiOk = {
  success: boolean;
  data: {
    ok: true;
    envelope: RequestAmountEnvelope | null;
    indicativeSend: number | null;
    minReceive?: number | null;
    maxReceive?: number | null;
    receiveCurrency?: string;
  };
};

/**
 * Prefer receive-leg bound for requester-facing copy; fall back to send.
 */
export const pickDisplayBound = (
  details: RequestAmountEnvelopeDetails,
  side: "min" | "max",
  fallbackCurrency: string,
): { amount: number; currency: string } | null => {
  if (side === "min") {
    if (typeof details.minReceive === "number") {
      return {
        amount: details.minReceive,
        currency:
          details.receiveCurrency?.toUpperCase() || fallbackCurrency,
      };
    }
    if (typeof details.min === "number") {
      return {
        amount: details.min,
        currency: details.currency?.toUpperCase() || fallbackCurrency,
      };
    }
    return null;
  }
  if (typeof details.maxReceive === "number") {
    return {
      amount: details.maxReceive,
      currency: details.receiveCurrency?.toUpperCase() || fallbackCurrency,
    };
  }
  if (typeof details.max === "number") {
    return {
      amount: details.max,
      currency: details.currency?.toUpperCase() || fallbackCurrency,
    };
  }
  return null;
};

/**
 * Pre-mint gate: same partner send envelope as request-money create / mint.
 * Throws only for unexpected errors; envelope breaches return `{ ok: false }`.
 */
export const checkRequestAmount = async (
  input: RequestAmountCheckInput,
): Promise<RequestAmountCheckOk | RequestAmountCheckFail> => {
  try {
    const { data } = await widgetApi.post<ApiOk>(
      "/widget/limits/request-amount-check",
      input,
    );
    return {
      ok: true,
      envelope: data.data.envelope,
      indicativeSend: data.data.indicativeSend,
      minReceive: data.data.minReceive,
      maxReceive: data.data.maxReceive,
      receiveCurrency: data.data.receiveCurrency,
    };
  } catch (error) {
    const api = getWidgetApiError(error);
    if (
      api?.code === "REQUEST_AMOUNT_BELOW_MIN" ||
      api?.code === "REQUEST_AMOUNT_ABOVE_MAX"
    ) {
      const details =
        api.details && typeof api.details === "object"
          ? (api.details as RequestAmountEnvelopeDetails)
          : {};
      return { ok: false, code: api.code, details };
    }
    throw error;
  }
};
